<template>
  <section class="benchmark-metrics">
    <page-sub-header title="Page Visits" class="mt-4" />
    <sme-card>
      <app-loading v-if="loading" />
      <template v-else>
        <sme-alert v-if="hasNoSector" level="info">
          Sector comparison not available - your company has not been assigned to a sector.
        </sme-alert>
        <template v-else-if="tableData.length">
          <p class="mb-3 text-muted">
            Comparing page visits against the <strong>{{ sector }}</strong> sector average. Percentages are based on
            <strong>{{ uniqueUsersFormatted }}</strong> unique visitors in the last complete month.
          </p>
          <b-table
            :items="tableData"
            :fields="TABLE_FIELDS"
            class="mb-0"
            table-class="table-minimal"
            responsive
            show-empty
          >
            <template #cell(current)="{ value }">
              <span class="metric-value">{{ formatPercentage(value) }}</span>
            </template>
            <template #cell(benchmark)="{ value }">
              <span class="metric-value">{{ formatPercentage(value) }}</span>
            </template>
            <template #cell(delta)="{ item }">
              <span class="metric-value" :class="getDeltaClass(item.delta)">{{ formatDeltaValue(item.delta) }}</span>
            </template>
          </b-table>
        </template>
        <p v-else class="my-2 text-center">No data to display.</p>
      </template>
    </sme-card>
  </section>
</template>

<script setup>
import { onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import AppLoading from '@/components/AppLoading.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import { TIME_SERIES } from '@/utils/chart';

const TABLE_FIELDS = [
  { key: 'product', label: 'Page' },
  { key: 'current', label: 'Current' },
  { key: 'benchmark', label: 'Benchmark' },
  { key: 'delta', label: 'Delta' },
];

const PRODUCT_MAPPINGS = {
  track_users: 'Track',
  save_users: getBankingInfo().save.label,
  stream_users: 'Pay',
  hub_users: 'Hub',
};

const loading = ref(true);
const sector = ref(null);
const tableData = ref([]);
const hasNoSector = ref(false);
const uniqueUsersFormatted = ref('-');

const formatPercentage = value => {
  if (value === null || value === undefined) {
    return '-';
  }
  return `${Math.round(value)}%`;
};

const formatDeltaValue = value => {
  if (value === null || value === undefined) {
    return '—';
  }
  const sign = value >= 0 ? '+' : '';
  return `${sign}${Math.round(value)}%`;
};

const getDeltaClass = value => {
  if (value == null) {
    return 'delta-neutral';
  }
  const rounded = Math.round(value);
  if (rounded === 0) {
    return 'delta-neutral';
  }
  return rounded > 0 ? 'delta-positive' : 'delta-neutral';
};

const fetchBenchmarkData = async () => {
  loading.value = true;

  try {
    const response = await ApiClient.getAppFeaturesMetrics(TIME_SERIES.MONTH_TO_MONTH, null, true);
    const { results, sector: sectorName } = response;

    if (!sectorName) {
      hasNoSector.value = true;
      loading.value = false;
      return;
    }

    sector.value = sectorName;

    if (!results?.length) {
      loading.value = false;
      return;
    }

    const uniqueUsersResult = results.find(result => result.id === 'unique_users');
    const uniqueUsers = uniqueUsersResult?.calculated?.last_complete || 0;
    uniqueUsersFormatted.value = uniqueUsers.toLocaleString('en-GB');

    const productIds = ['track_users', 'save_users', 'stream_users', 'hub_users'];
    const rows = [];

    productIds.forEach(productId => {
      const productResult = results.find(result => result.id === productId);
      if (productResult && PRODUCT_MAPPINGS[productId]) {
        const lastValue = productResult.calculated?.last_complete || 0;
        const productCurrent = uniqueUsers ? (lastValue / uniqueUsers) * 100 : 0;
        const productSectorAvg = productResult.sector_averages?.[productResult.sector_averages.length - 1] ?? null;

        const delta = productCurrent != null && productSectorAvg != null ? productCurrent - productSectorAvg : null;

        rows.push({
          product: PRODUCT_MAPPINGS[productId],
          current: productCurrent,
          benchmark: productSectorAvg,
          delta,
        });
      }
    });

    tableData.value = rows;
  } catch (error) {
    console.error('Failed to fetch benchmark data:', error);
  }

  loading.value = false;
};

onBeforeMount(() => fetchBenchmarkData());
</script>

<style lang="scss" scoped>
.benchmark-metrics {
  .metric-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--palette-color-brand-primary);
  }

  .delta-positive {
    color: var(--palette-color-success);
  }

  .delta-neutral {
    color: var(--palette-color-default-lighten-20);
  }
}
</style>
